/* eslint-disable -- vendored file, it originally had no types and a different eslint config */

// react-select uses emotion and allows customization
// https://react-select.com/styles

import { theme } from "@netlify/tailwind-config/theme";

const selectStyles = (hasSearchStyles: any) => ({
  control: (provided: any, { menuIsOpen, isDisabled, isFocused }: any) => {
    const base = {
      backgroundColor: "rgb(var(--colorWhite))",
      border: `${theme("borderWidth.DEFAULT")} solid rgb(var(--colorGray))`,
      borderRadius: theme("borderRadius.sm"),
      color: "rgb(var(--colorBlack))",
      fontSize: theme("fontSize.base"),
      fontWeight: theme("fontWeight.medium"),
      lineHeight: theme("lineHeight.tight"),
      margin: "0",
      cursor: isDisabled ? "not-allowed" : "pointer",
      height: "auto",
      minHeight: "36px",
      pointerEvents: "auto",
      position: "relative",
      width: "100%",
      ".tw-dark &": {
        backgroundColor: "rgb(var(--colorBlack))",
        borderColor: "rgb(var(--colorGrayLight))",
        "&:hover": {
          borderColor: isDisabled
            ? "rgb(var(--colorGrayLight))"
            : "rgb(var(--colorGrayLighter))",
        },
      },
      "&:hover": {
        borderColor: isDisabled
          ? "rgb(var(--colorGray))"
          : "rgb(var(--colorGrayDarker))",
      },
    };
    const focused = isFocused
      ? {
          border: `${theme(
            "borderWidth.DEFAULT",
          )} solid rgb(var(--colorGrayDarkest))`,
          boxShadow:
            "0 0 0 2px rgb(var(--colorTeal)), 0 0 1px 0 rgb(var(--colorGrayDarkest))",
          outline: "2px solid transparent",
          transition: "0.2s ease",
          "&:hover": {
            borderColor: "rgb(var(--colorGrayDarkest))",
          },
          ".tw-dark &": {
            backgroundColor: "rgb(var(--colorBlack))",
            border: `${theme(
              "borderWidth.DEFAULT",
            )} solid rgb(var(--colorGrayLightest))`,
          },
        }
      : null;
    const menuOpen = menuIsOpen
      ? {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }
      : null;
    return { ...provided, ...base, ...focused, ...menuOpen };
  },
  valueContainer: (provided: any, { hasValue, isMulti }: any) => ({
    ...provided,
    textAlign: "left",
    minHeight: `calc(36px - ${theme("borderWidth.DEFAULT")} * 2)`,
    padding: hasSearchStyles
      ? `0 0 0 calc(${theme("spacing.4")} - (${theme(
          "borderWidth.DEFAULT",
        )} * 2))`
      : hasValue && isMulti
        ? ` 0 0 calc(${theme("spacing.1")} + ${theme(
            "borderWidth.DEFAULT",
          )}) +  0`
        : `0 0 0 calc(${theme("spacing.4")} - (${theme(
            "borderWidth.DEFAULT",
          )} * 2))`,
    ...(hasSearchStyles && {
      marginLeft: `calc(${theme("spacing.6")} - (${theme(
        "borderWidth.DEFAULT",
      )} * 2))`,
    }),
  }),
  singleValue: (provided: any) => ({
    ...provided,
    marginRight: "0",
    paddingRight: theme("spacing.1"),
    ".tw-dark &": {
      color: "rgb(var(--colorWhite))",
    },
  }),
  input: (provided: any, { hasValue, isDisabled = false, isMulti }: any) => {
    const base = {
      input: {
        height: "auto",
        background: "none",
        pointerEvents: isDisabled ? "none" : "auto",
        "&:focus": {
          border: "0",
          boxShadow: "none",
        },
      },
      ".tw-dark &": {
        color: "rgb(var(--colorWhite))",
      },
    };
    const multi = isMulti
      ? {
          margin: hasValue
            ? `calc(${theme("spacing.2")} - ${theme(
                "borderWidth.DEFAULT",
              )}) 0 0 calc(${theme("spacing.2")} - ${theme(
                "borderWidth.DEFAULT",
              )})`
            : "0",
          padding: hasValue
            ? `0 ${theme("spacing.1")} 0`
            : `0 ${theme("spacing.1")} 0 calc(${theme("spacing.1")} - ${theme("borderWidth.DEFAULT")})`,
        }
      : null;
    return { ...provided, ...base, ...multi };
  },
  placeholder: (provided: any) => ({
    ...provided,
    color: "rgb(var(--colorTextMuted))",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    ...(hasSearchStyles && { marginLeft: "2px" }),
  }),
  menu: (provided: any) => ({
    ...provided,
    border: `calc(1px / 2) solid rgb(var(--colorGrayLight))`,
    borderBottomLeftRadius: theme("borderRadius.sm"),
    borderBottomRightRadius: theme("borderRadius.sm"),
    borderTopLeftRadius: "0",
    borderTopRightRadius: "0",
    boxShadow: "0 1px 0 rgba(0 0 0 / 6%)",
    left: "0",
    marginTop: "0",
    maxHeight: `calc(36px * 6.5 + 3px)` /* each option is 36px tall, we want to allow 6.5 visible in default view */,
    right: "0",
    zIndex: 999, // = theme('zIndex.dropdown')
    ".tw-dark &": {
      backgroundColor: "rgb(var(--colorGrayDarkest))",
      borderColor: "rgb(var(--colorGrayDarkest))",
    },
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: `calc(36px * 6.5 + 3px)` /* each option is 36px tall, we want to allow 6.5 visible in default view */,
    padding: "0",
  }),
  option: (provided: any, { isFocused, isDisabled }: any) => ({
    ...provided,
    color: "rgb(var(--colorGrayDarkest))",
    backgroundColor: isFocused ? "rgb(var(--colorGrayLighter))" : "",
    cursor: isDisabled ? "not-allowed" : "pointer",
    opacity: isDisabled ? "0.38" : "1",
    display: "flex",
    fontWeight: theme("fontWeight.medium"),
    lineHeight: theme("lineHeight.tight"),
    justifyContent: "space-between",
    overflowWrap: "anywhere",
    padding: hasSearchStyles
      ? `${theme("spacing.2")} ${theme("spacing.4")} ${theme("spacing.2")} calc(${theme("spacing.6")} * 1.5 - 1px)`
      : `${theme("spacing.2")} ${theme("spacing.4")}`,
    ".tw-dark &": {
      backgroundColor: isFocused ? "rgb(var(--colorGrayDarker))" : "",
      color: "rgb(var(--colorGrayLightest))",
    },
  }),
  indicatorsContainer: (provided: any, { isDisabled }: any) => ({
    ...provided,
    alignSelf: "flex-start",
    height: `calc(36px - (${theme("borderWidth.DEFAULT")} * 2))`,
    opacity: isDisabled ? "0.38" : "1",
    paddingRight: "1px",
    svg: {
      transition: "transform 0.1s cubic-bezier(0.07, 0.49, 0, 1.05)",
      color: "rgb(var(--colorBlack))",
      ".tw-dark &": {
        color: "rgb(var(--colorWhite))",
      },
    },
  }),
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    paddingLeft: `calc(${theme("spacing.2")} - ${theme(
      "borderWidth.DEFAULT",
    )})`,
    ...(hasSearchStyles && { display: "none" }),
    ...(state.selectProps.menuIsOpen && {
      svg: { transform: "rotate(-180deg)" },
    }),
  }),
  clearIndicator: (provided: any) => ({
    ...provided,
    paddingLeft: `calc(${theme("spacing.2")} - ${theme(
      "borderWidth.DEFAULT",
    )})`,
  }),
  loadingIndicator: () => ({
    display: "none",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  multiValue: (provided: any, { isFocused }: any) => ({
    ...provided,
    backgroundColor: isFocused
      ? "rgb(var(--colorGrayLight))"
      : "rgb(var(--colorGrayLighter))",
    borderRadius: `calc(${theme("borderRadius.sm")} / 2)`,
    fontSize: theme("fontSize.sm"),
    margin: `calc(${theme("spacing.1")} + ${theme(
      "borderWidth.DEFAULT",
    )}) 0 0 calc(${theme("spacing.1")} + ${theme("borderWidth.DEFAULT")})`,
    "&:hover": {
      backgroundColor: "rgb(var(--colorGrayLight))",
    },
    ".tw-dark &": {
      backgroundColor: isFocused
        ? "rgb(var(--colorGrayDark))"
        : "rgb(var(--colorGray))",
      color: "rgb(var(--colorWhite))",
      "&:hover": {
        backgroundColor: "rgb(var(--colorGrayDark))",
      },
    },
  }),
  multiValueLabel: (provided: any) => ({
    ...provided,
    fontSize: "inherit",
    fontWeight: theme("fontWeight.bold"),
    paddingRight: theme("spacing.1"),
    paddingLeft: theme("spacing.2"),
    paddingTop: 0,
    paddingBottom: 0,
    height: theme("spacing.6"),
    lineHeight: theme("lineHeight.6"),
    ".tw-dark &": {
      color: "inherit",
      backgroundColor: "transparent",
    },
  }),
  multiValueRemove: (provided: any) => ({
    ...provided,
    "&, &:hover, .tw-dark &": {
      color: "inherit",
      backgroundColor: "transparent",
    },
  }),
  noOptionsMessage: (provided: any) => ({
    ...provided,
    color: "rgb(var(--colorTextMuted))",
  }),
});

export default selectStyles;
